import { cloneDeep } from 'lodash'

import { isEmpty, proxy } from '@/utils'

import render from './render'

const createFirstItem = () => {
  return {
    key: 1,
    price: undefined
  }
}

export default {
  name: 'PriceRangesDialog',

  mixins: [ proxy() ],

  props: {
    data: {
      type: Array,
      default: () => {
        return []
      }
    }
  },

  data() {
    return {
      items: []
    }
  },

  computed: {
    filledItems() {
      return this.filterFilledItems(this.items)
    },
    normalizedItems() {
      return this.filledItems.map(item => {
        return this.normalizeItem(item)
      }).filter(Boolean)
    },

    firstKeyIndex() {
      return this.items.findIndex(({ key }) => {
        return this.parseKey(key) === 1
      })
    },

    hasFirstKey() {
      return this.firstKeyIndex !== -1
    },
    hasIncompleteItems() {
      return this.items.some(item => {
        return this.isPartiallyFilledItem(item)
      })
    },
    hasInvalidItems() {
      return this.filledItems.some(item => {
        return !this.normalizeItem(item)
      })
    },
    hasDuplicateKeys() {
      const keys = this.normalizedItems.map(({ key }) => {
        return key
      })

      return new Set(keys).size !== keys.length
    },
    hasValidFirstPrice() {
      return this.normalizedItems.some(({ key }) => {
        return key === 1
      })
    },

    canSave() {
      return (
        !this.hasIncompleteItems &&
        !this.hasInvalidItems &&
        !this.hasDuplicateKeys &&
        this.hasValidFirstPrice
      )
    }
  },

  watch: {
    proxy(value) {
      if (value) {
        this.syncData()
      }
    },

    data: {
      handler() {
        if (!this.proxy) {
          this.syncData()
        }
      },
      deep: true
    },

    items: {
      handler(items, previousItems) {
        this.ensureFirstKey(previousItems)
      },
      deep: true
    }
  },

  mounted() {
    this.syncData()
  },

  methods: {
    ensureFirstKey(previousItems) {
      if (!this.hasFirstKey) {
        if (this.items.length) {
          let firstItem = createFirstItem()
          if (Array.isArray(previousItems)) {
            const previousFirstItem = previousItems.find(({ key }) => {
              return this.parseKey(key) === 1
            })
            if (previousFirstItem) {
              firstItem = cloneDeep(previousFirstItem)
            }
          }

          this.items = [ firstItem, ...this.items ]

          return
        }

        this.items = [ createFirstItem() ]
      }
    },

    syncData() {
      if (Array.isArray(this.data)) {
        this.items = cloneDeep(this.data)

        return
      }

      this.items = []
    },

    isPartiallyFilledItem({ key, price }) {
      const hasKey = !isEmpty(key)
      const hasPrice = !isEmpty(price)

      return (hasKey || hasPrice) && !(hasKey && hasPrice)
    },

    filterFilledItems(data) {
      return data.filter(({ key, price }) => {
        return !isEmpty(key) && !isEmpty(price)
      })
    },

    parseKey(value) {
      if (typeof value === 'number') {
        if (Number.isInteger(value) && value > 0) {
          return value
        }

        return undefined
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim()
        if (!/^[1-9]\d*$/.test(trimmedValue)) {
          return undefined
        }

        return Number(trimmedValue)
      }

      return undefined
    },

    parsePrice(value) {
      if (typeof value === 'number') {
        if (Number.isFinite(value) && value >= 0) {
          return value
        }

        return undefined
      }

      if (typeof value === 'string') {
        const normalizedValue = value.trim().replace(',', '.')
        if (normalizedValue === '') {
          return undefined
        }

        const parsedValue = Number(normalizedValue)
        if (Number.isFinite(parsedValue) && parsedValue >= 0) {
          return parsedValue
        }
      }

      return undefined
    },

    normalizeItem({ key, price }) {
      const normalizedKey = this.parseKey(key)
      const normalizedPrice = this.parsePrice(price)
      if (isEmpty(normalizedKey) || isEmpty(normalizedPrice)) {
        return undefined
      }

      return {
        key: normalizedKey,
        price: normalizedPrice
      }
    },

    getSortedItems(data) {
      return cloneDeep(data).sort((firstItem, secondItem) => {
        return firstItem.key - secondItem.key
      })
    },

    saveHandler() {
      if (this.canSave) {
        this.$emit('save', this.getSortedItems(this.normalizedItems))
        this.proxy = false
      }
    }
  },

  render
}
